import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { InfoOutlined } from "@material-ui/icons";
import CardTitle from "@saleor/components/CardTitle";
import Hr from "@saleor/components/Hr";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { useRemittanceRunnerUploadReceipt } from "@saleor/remittanceRunner/mutations";
import { GetRemittance_remittance_receiptImage } from "@saleor/remittanceRunner/types/GetRemittance";
import { RemittanceRunnerUploadReceipt } from "@saleor/remittanceRunner/types/RemittanceRunnerUploadReceipt";
import classNames from "classnames";
import React from "react";
import Dropzone from "react-dropzone";
import { useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    centerItems: {
      alignItems: "center",
      display: "flex",
      justifyContent: "center"
    },
    cursorHand: {
      cursor: "pointer"
    },
    descriptionLabel: {
      margin: "5px 0px 5px 0px"
    },
    fileField: {
      display: "none"
    },
    fileUploadField: {
      alignItems: "center",
      display: "flex",
      height: "150px",
      justifyContent: "center",
      padding: "0!important"
    },
    image: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      width: "100%"
    },
    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      position: "relative",
      width: "100%"
    },
    imageGridContainer: {
      position: "relative"
    },
    imageOverlay: {
      background: "rgba(0, 0, 0, 0.6)",
      cursor: "move",
      display: "none",
      height: "100%",
      left: 0,
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageOverlayShow: {
      "&$imageOverlay": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
      }
    },
    imageOverlayToolbar: {
      display: "flex",
      position: "absolute",
      right: 0,
      top: 0
    },
    info: {
      color: "#0937f4",
      fontSize: 17,
      fontWeight: "bold"
    },
    infoGuidelines: {
      fontSize: 13,
      marginLeft: "10px"
    },
    photosIcon: {
      height: "64px",
      margin: "0 auto",
      width: "64px"
    },
    textBold: {
      fontWeight: "bold"
    },
    textDisabled: {
      "& span": {
        color: "#bdbdbd !important"
      },
      color: "#bdbdbd !important"
    },
    textPrimary: {
      "& span": {
        color: "#0030FF"
      },
      color: "#0030FF"
    },
    uploadRoot: {
      fontSize: 12,
      fontWeight: 600,
      padding: "15px",
      textAlign: "center"
    }
  }),
  { name: "RemittanceRunnerRemittanceProof" }
);

export interface RemittanceRunnerRemittanceProofProps {
  isDispatcher: boolean;
  remittanceID: string;
  receiptImage: GetRemittance_remittance_receiptImage;
  refetch: () => void;
}

const fileSizeLimit = 4.0e6;

const RemittanceRunnerRemittanceProof: React.FC<RemittanceRunnerRemittanceProofProps> = props => {
  const { isDispatcher, remittanceID, receiptImage, refetch } = props;
  const classes = useStyles(props);
  const notify = useNotifier();
  const intl = useIntl();

  const upload = React.useRef(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const checkFile = e => {
    const file = e[0];
    setIsSaving(true);
    const isValidFileType =
      file.type.includes("image/jpeg") ||
      file.type.includes("image/jpg") ||
      file.type.includes("image/png");
    const isValidFileSize = file.size < fileSizeLimit;

    if (!isValidFileType) {
      notify({
        status: "error",
        text: "The format is not acceptable. Check the format and upload again!"
      });
      setIsSaving(false);
    } else if (!isValidFileSize) {
      notify({
        status: "error",
        text: "Image size is too big! Try compressing."
      });
      setIsSaving(false);
    } else {
      handleReceiptSaving(file);
    }
  };

  const handleUploadReceiptCompleted = (
    data: RemittanceRunnerUploadReceipt
  ) => {
    if (data?.remittanceUploadReceipt?.errors?.length === 0) {
      notify({
        status: "success",
        text: "Image uploaded successfully"
      });
      refetch();
    } else if (data?.remittanceUploadReceipt?.errors?.length > 0) {
      data.remittanceUploadReceipt.errors.forEach(error => {
        notify({
          status: "error",
          text: error.message
        });
      });
    }
    setIsSaving(false);
  };

  const handleReceiptSaving = async (file: any) => {
    const result = await uploadReceipt({
      variables: {
        id: remittanceID,
        receiptImage: file
      }
    });
    return result?.data?.remittanceUploadReceipt?.errors;
  };

  const [uploadReceipt] = useRemittanceRunnerUploadReceipt({
    onCompleted: handleUploadReceiptCompleted
  });

  const disableUpload = isSaving || receiptImage?.url !== undefined;

  const openImageInNewTab = imageUrl => {
    window.open(imageUrl, "_blank");
  };

  const getContentToRender = () => {
    let contentToRender;

    if (maybe(() => isSaving)) {
      contentToRender = (
        <div className={`${classes.imageContainer} ${classes.centerItems}`}>
          <CircularProgress className={classes.image} size={32} />
        </div>
      );
    } else if (maybe(() => receiptImage?.url !== undefined)) {
      contentToRender = (
        <div
          className={`${classes.imageContainer} ${classes.cursorHand}`}
          onClick={() => openImageInNewTab(receiptImage?.url)}
        >
          <img
            alt={receiptImage?.alt}
            className={classes.image}
            src={receiptImage?.url}
          />
        </div>
      );
    } else {
      contentToRender = (
        <div className={classes.uploadRoot}>
          <ImageIcon className={classes.photosIcon} />
          <p
            className={
              !(isDispatcher || disableUpload)
                ? `${classes.descriptionLabel} ${classes.textDisabled}`
                : `${classes.descriptionLabel}`
            }
          >
            DROP HERE TO UPLOAD
          </p>
        </div>
      );
    }

    return contentToRender;
  };

  return (
    <Card>
      <CardTitle
        title="Proof of Remittance"
        toolbar={
          <>
            <Button
              id="proofOfRemittanceBtn"
              variant="text"
              data-test="button-upload-image"
              onClick={() => upload.current.click()}
              disabled={!isDispatcher || disableUpload}
              className={
                !isDispatcher || disableUpload
                  ? `${classes.textDisabled}`
                  : `${classes.textPrimary}`
              }
            >
              {intl.formatMessage(commonMessages.uploadImage)}
            </Button>
            {isDispatcher && !disableUpload && (
              <input
                className={classes.fileField}
                id="fileUpload"
                type="file"
                accept="image/jpeg, image/jpg, image/png"
                multiple={false}
                ref={upload}
                onChange={e =>
                  e.target.files.length > 0 && checkFile(e.target.files)
                }
              />
            )}
          </>
        }
      />
      <div className={classNames(classes.imageGridContainer)}>
        <CardContent>
          <Dropzone
            onDrop={isDispatcher && !disableUpload ? checkFile : null}
            disableClick={true}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <CardContent className={classes.fileUploadField}>
                  {isDispatcher && !disableUpload && (
                    <input
                      {...getInputProps()}
                      id="fileUpload"
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      multiple={false}
                      ref={upload}
                      onChange={e =>
                        e.target.files.length > 0 && checkFile(e.target.files)
                      }
                    />
                  )}

                  {getContentToRender()}
                </CardContent>
              </div>
            )}
          </Dropzone>
        </CardContent>

        {isDispatcher && (
          <>
            <Hr />
            <CardContent>
              <Grid>
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap"
                  }}
                >
                  <InfoOutlined color="primary" />
                  <Typography className={classes.info}>
                    {" "}
                    Upload Guidelines:
                  </Typography>
                </div>
              </Grid>
              <div>
                <span className={classes.infoGuidelines}>
                  1. Image file should{" "}
                  <span className={classes.textBold}>
                    not be greater than 4MB.
                  </span>
                </span>
                <br />
                <span className={classes.infoGuidelines}>
                  2. Only{" "}
                  <span className={classes.textBold}>
                    JPG, JPEG and PNG files
                  </span>{" "}
                  are acceptable.
                </span>
              </div>
            </CardContent>
          </>
        )}
      </div>
    </Card>
  );
};
RemittanceRunnerRemittanceProof.displayName = "RemittanceRunnerRemittanceProof";
export default RemittanceRunnerRemittanceProof;
